import type { ApproverSource } from '@clockwise/clients/approval-policies';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Plus, ShieldCheck, User as UserIcon, Users } from 'lucide-react';
import { useState } from 'react';
import { UserAutocomplete } from '@/components/custom/user-autocomplete';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { systemRolesApi } from '@/lib/api';
import { popoverScrollFix } from '@/lib/popover-scroll-fix';
import { queryKeys } from '@/lib/query-keys';
import { warnIfTruncated } from '@/lib/warn-if-truncated';
import { APPROVER_TYPE_LABELS, ApproverSourceBadge } from './approver-source-badge';

/**
 * Two-stage approver picker. Click "+ Add approver" → small dropdown of
 * kinds. Terminal kinds (users_manager, customer_team_lead) add a
 * chip immediately. User and System Role open stage 2 — UserAutocomplete
 * for users, a small static list for roles. The same popover transitions
 * between stage 1 ↔ stage 2 via the `← back` link.
 *
 * Already-selected entries are filtered out of stage 2 to prevent
 * duplicates.
 *
 * See typescript/docs/approvals.md for the design.
 */

interface Props {
  value: ApproverSource[];
  onChange: (next: ApproverSource[]) => void;
  disabled?: boolean;
}

type Stage = 'kind' | 'user' | 'role';

export function ApproverSourcePicker({ value, onChange, disabled }: Props): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>('kind');

  const handleOpenChange = (next: boolean) => {
    // Reset the popover to stage 1 every time it OPENS — not on close.
    // Resetting on close used to need a setTimeout to wait out the
    // close animation; resetting on open avoids that race entirely.
    if (next) setStage('kind');
    setOpen(next);
  };

  const closeAndReset = () => {
    setOpen(false);
  };

  const addSource = (source: ApproverSource) => {
    // Dedupe — don't add the same source twice.
    const exists = value.some((existing) => sameSource(existing, source));
    if (!exists) onChange([...value, source]);
    closeAndReset();
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    // Chips + "Add approver" share one wrapping row — keeps each step
    // compact when it has 1–2 approvers. The picker only breaks to its
    // own line when the chip set is long enough to wrap.
    <div className="flex flex-wrap items-center gap-1.5">
      {value.map((source, i) => (
        // `sourceKey(source)` is unique per non-duplicate source (we
        // dedupe on add). No need for an additional index suffix.
        <ApproverSourceBadge
          key={sourceKey(source)}
          source={source}
          onRemove={disabled ? undefined : () => removeAt(i)}
        />
      ))}

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            // `rounded-full` stated directly: `PopoverTrigger asChild` lets
            // Radix's Slot clobber this Button's own `data-slot="button"`
            // with the trigger's `data-slot="popover-trigger"`, so the
            // global `[data-slot='button']` pill-radius rule never matches.
            className="h-7 rounded-full"
            disabled={disabled}
          >
            <Plus className="mr-1.5 size-3.5" />
            Add approver
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          {stage === 'kind' && (
            <KindStage
              value={value}
              onPickType={(type) => addSource({ kind: 'type', type })}
              onGotoUser={() => setStage('user')}
              onGotoRole={() => setStage('role')}
            />
          )}
          {stage === 'user' && (
            <UserStage
              excludeIds={value.flatMap((s) => (s.kind === 'user' ? [s.userId] : []))}
              onBack={() => setStage('kind')}
              onPick={(userId) => addSource({ kind: 'user', userId })}
            />
          )}
          {stage === 'role' && (
            <RoleStage
              excludeIds={value.flatMap((s) => (s.kind === 'system_role' ? [s.roleId] : []))}
              onBack={() => setStage('kind')}
              onPick={(roleId) => addSource({ kind: 'system_role', roleId })}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ── Stage 1: kind picker ─────────────────────────────────────────────────

interface KindStageProps {
  value: ApproverSource[];
  onPickType: (type: Extract<ApproverSource, { kind: 'type' }>['type']) => void;
  onGotoUser: () => void;
  onGotoRole: () => void;
}

function KindStage({
  value,
  onPickType,
  onGotoUser,
  onGotoRole,
}: KindStageProps): React.ReactElement {
  const hasType = (t: Extract<ApproverSource, { kind: 'type' }>['type']) =>
    value.some((s) => s.kind === 'type' && s.type === t);
  return (
    <Command>
      {/* popoverScrollFix: portaled list; keeps a wrapping Sheet/Dialog's
          scroll-lock from eating wheel events. See lib/popover-scroll-fix.ts. */}
      <CommandList {...popoverScrollFix}>
        <CommandGroup heading="Types">
          {(['users_manager', 'customer_team_lead'] as const).map((t) => (
            <CommandItem
              key={t}
              value={`type-${t}`}
              onSelect={() => onPickType(t)}
              disabled={hasType(t)}
            >
              <Users className="mr-2 size-3.5 text-muted-foreground" />
              {APPROVER_TYPE_LABELS[t]}
              {hasType(t) && <span className="ml-auto text-xs text-muted-foreground">added</span>}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup>
          <CommandItem value="user-stage" onSelect={onGotoUser}>
            <UserIcon className="mr-2 size-3.5 text-muted-foreground" />
            Specific user…
            <ChevronRight className="ml-auto size-3.5 text-muted-foreground" />
          </CommandItem>
          <CommandItem value="role-stage" onSelect={onGotoRole}>
            <ShieldCheck className="mr-2 size-3.5 text-muted-foreground" />
            System role…
            <ChevronRight className="ml-auto size-3.5 text-muted-foreground" />
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

// ── Stage 2a: user picker ────────────────────────────────────────────────

interface UserStageProps {
  excludeIds: readonly string[];
  onBack: () => void;
  onPick: (userId: string) => void;
}

function UserStage({ excludeIds, onBack, onPick }: UserStageProps): React.ReactElement {
  return (
    <div className="p-2">
      <BackButton onClick={onBack} />
      <UserAutocomplete
        value={null}
        onChange={(u) => {
          if (u) onPick(u.id);
        }}
        excludeIds={excludeIds}
        placeholder="Search employees…"
      />
    </div>
  );
}

// ── Stage 2b: system role picker ────────────────────────────────────────

interface RoleStageProps {
  excludeIds: readonly string[];
  onBack: () => void;
  onPick: (roleId: string) => void;
}

function RoleStage({ excludeIds, onBack, onPick }: RoleStageProps): React.ReactElement {
  const excludeSet = new Set(excludeIds);
  const query = useQuery({
    queryKey: queryKeys.systemRoles.list({
      sortField: 'name',
      sortDirection: 'asc',
      limit: 100,
      offset: 0,
    }),
    queryFn: () =>
      systemRolesApi.list({
        sortField: 'name',
        sortDirection: 'asc',
        active: true,
        limit: 100,
        offset: 0,
      }),
  });
  warnIfTruncated(query.data, 'approver-source system-role picker');
  const roles = (query.data?.data ?? []).filter((r) => !excludeSet.has(r.id));
  return (
    <Command>
      <div className="p-1">
        <BackButton onClick={onBack} />
      </div>
      <CommandInput placeholder="Filter roles…" />
      <CommandList {...popoverScrollFix}>
        {query.isLoading && <div className="px-3 py-2 text-sm text-muted-foreground">Loading…</div>}
        {!query.isLoading && roles.length === 0 && <CommandEmpty>No roles available.</CommandEmpty>}
        <CommandGroup>
          {roles.map((role) => (
            <CommandItem key={role.id} value={role.name} onSelect={() => onPick(role.id)}>
              <ShieldCheck className="mr-2 size-3.5 text-muted-foreground" />
              {role.name}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function BackButton({ onClick }: { onClick: () => void }): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-1 inline-flex items-center text-xs text-muted-foreground hover:text-foreground"
    >
      ← back
    </button>
  );
}

function sameSource(a: ApproverSource, b: ApproverSource): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'user' && b.kind === 'user') return a.userId === b.userId;
  if (a.kind === 'system_role' && b.kind === 'system_role') return a.roleId === b.roleId;
  if (a.kind === 'type' && b.kind === 'type') return a.type === b.type;
  return false;
}

function sourceKey(s: ApproverSource): string {
  if (s.kind === 'user') return `user:${s.userId}`;
  if (s.kind === 'system_role') return `role:${s.roleId}`;
  return `type:${s.type}`;
}
